/**
 * Gets a formatted customer name.
 *
 * If displayName is shorter than 16 chars then it returns that.
 * If displayName is longer than 15 chars then it returns the
 * first initial and last name
 * */

import get from 'lodash/get';
import dayjs from 'dayjs';
import weekDay from 'dayjs/plugin/weekday';
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import toNumber from 'lodash/toNumber';
import { v4 as uuidv4 } from 'uuid';

import { Intervals } from '../../lib/shop/constants';
import {
  AdjustmentTypes,
  PricingPolicy,
  discountTypeLookUp,
  CycleDiscounts,
  CycleDiscountsApi,
  PricingPolicyApi,
} from '../../lib/products';
import { getCycleDiscount } from '../../lib/subscriptionContracts/pricing';
import { BatchAnchors, SellingPlan } from '../../hooks/types';
import { BatchingAPI } from './BatchingSubscriptionSection';
import { getGidFromIdForResource } from '../../utils/gid-utils';
import {
  isDynamicBundle,
  isDynamicLine,
} from '../../components/Subscriptions/utilsDynamicBundles';
import { SC_LINE_MODS_ATTRIBUTE } from '../../lib/scLineMods';
import { LimitTypesEnum } from '../BundleSettings/types';

export const CUSTOM_INTERVAL_OPTION = {
  label: 'Custom...',
  value: 'custom',
};

export const DEFAULT_INTERVAL_OPTIONS = [
  {
    label: '2 weeks',
    value: '2weeks',
  },
  {
    label: '1 month',
    value: '1month',
  },
  {
    label: '2 months',
    value: '2months',
  },
  CUSTOM_INTERVAL_OPTION,
];

const CUSTOM_INTERVALS = {
  '2weeks': {
    interval: Intervals.Week,
    intervalCount: 2,
  },

  '1month': {
    interval: Intervals.Month,
    intervalCount: 1,
  },
  '2months': {
    interval: Intervals.Month,
    intervalCount: 2,
  },
  custom: {
    intervalCount: 1,
    interval: Intervals.Month,
  },
};

const INVENTORY_NOT_MANAGED = 'NOT_MANAGED';
const INVENTORY_POLICY_CONTINUE = 'CONTINUE';

interface ProductToAdd {
  productVariantId: string;
  quantity: number;
  currentPrice: string;
  customAttributes?: { key: string; value: string }[];
  pricingPolicy?: {
    basePrice: string;
    cycleDiscounts: {
      adjustmentType: string;
      adjustmentValue: {
        fixedValue?: number;
        percentage?: number;
      };
      afterCycle: number;
      computedPrice: string;
    };
  };
}

interface ProductToAddWithName extends ProductToAdd {
  displayName: string;
  isOneTimeAddon?: boolean;
}

interface Customer {
  displayName: string;
  firstName: string;
  lastName: string;
}

type SortDirection = 'asc' | 'desc';

dayjs.extend(weekDay);

export const getCustomerName = (
  customer: Customer = {
    displayName: '',
    firstName: '',
    lastName: '',
  },
): string => {
  let customerName = customer.displayName;
  const { firstName, lastName } = customer;
  if (customerName.length > 16 && (firstName || lastName)) {
    customerName =
      firstName && lastName
        ? `${firstName[0]}. ${lastName}`
        : firstName || lastName;
  }
  return customerName;
};

const toTimeStamp = (isoDate: string): number => {
  const date = new Date(isoDate);
  return date.getTime();
};

/**
 * Gets a collection, a ISO date field and a sort direction and returns the collection
 * sorted by the field.
 *
 * If the field is a falsy value, it's concatenated at the end
 */
export const orderByDateField = <Collection extends Record<string, unknown>>(
  collection: Collection[] = [],
  field: keyof Collection,
  sortDirection: SortDirection = 'desc',
): Collection[] => {
  if (!(field && collection)) return collection;
  return collection
    .filter((item) => !!item[field])
    .sort((element: Collection, nextElement: Collection) => {
      const nextDate: number = toTimeStamp(nextElement[field] as string);
      const currentDate: number = toTimeStamp(element[field] as string);

      if (sortDirection === 'asc') {
        return currentDate - nextDate;
      }

      return nextDate - currentDate;
    })
    .concat(collection.filter((item) => !item[field]));
};

export const countSubscriptionsOnHold = (
  subscriptions: ApiSubscription[] = [],
): number =>
  subscriptions?.filter(
    (subscription) =>
      new Date(subscription.NextBillingDate) <= new Date() &&
      subscription.SubscriptionStatus === 'ACTIVE' &&
      !subscription.IsBilling &&
      !subscription.IsUpdating &&
      !subscription.IsInDunning &&
      !subscription.IsBillingScheduled,
  ).length;

export const listFormat = (list: string[], and = ', '): string =>
  list?.length
    ? list.reduce(
        (memo, current, index, array) =>
          memo + (index < array.length - 1 ? ', ' : `${and}`) + current,
      )
    : '';

const calculatePercentageDiscount = (basePrice, discount = 0) =>
  parseFloat(basePrice) * (1 - discount / 100);

const calculateFixedDiscount = (basePrice, discount = 0) => {
  const discounted = parseFloat(basePrice) - discount;
  return discounted < 0 ? 0 : discounted;
};

export const calculateDiscountedPriceFromPricingPolicy = ({
  pricingPolicy,
  variantPrice,
}) => {
  let result = 0;
  if (pricingPolicy) {
    switch (pricingPolicy.adjustmentType) {
      case AdjustmentTypes.PERCENTAGE:
        {
          result = calculatePercentageDiscount(
            variantPrice,
            pricingPolicy.adjustmentValue.percentage,
          );
        }
        break;

      case AdjustmentTypes.FIXED_AMOUNT:
        {
          result = calculateFixedDiscount(
            variantPrice,
            parseFloat(pricingPolicy.adjustmentValue.amount),
          );
        }
        break;
    }
    return result;
  }
  return parseFloat(variantPrice);
};

export const calculateDiscountedPrice = (
  pricingPolicy: PricingPolicy,
  variantPrice: string,
): number => {
  let result = 0;
  if (pricingPolicy?.cycleDiscounts?.length > 0) {
    switch (pricingPolicy.cycleDiscounts[0].adjustmentType) {
      case AdjustmentTypes.PERCENTAGE:
        {
          result = calculatePercentageDiscount(
            variantPrice,
            pricingPolicy.cycleDiscounts[0].adjustmentValue.percentage,
          );
        }
        break;

      case AdjustmentTypes.FIXED_AMOUNT:
        {
          result = calculateFixedDiscount(
            variantPrice,
            parseFloat(pricingPolicy.cycleDiscounts[0].adjustmentValue.amount),
          );
        }
        break;
    }
    return result;
  }
  return parseFloat(variantPrice);
};

export const getInitialSelectedDiscount = (
  pricingPolicy: PricingPolicy,
): { type: string; amount: string } => {
  if (pricingPolicy?.cycleDiscounts?.length > 0) {
    switch (pricingPolicy.cycleDiscounts[0].adjustmentType) {
      case AdjustmentTypes.PERCENTAGE:
        return {
          type: 'percentage',
          amount:
            pricingPolicy.cycleDiscounts[0].adjustmentValue?.percentage.toFixed(
              2,
            ),
        };

      case AdjustmentTypes.FIXED_AMOUNT: {
        return {
          type: 'fixed',
          amount:
            pricingPolicy.cycleDiscounts[0].adjustmentValue.amount ||
            pricingPolicy.cycleDiscounts[0].adjustmentValue.fixedValue ||
            '0',
        };
      }
    }
  }
  return { type: 'percentage', amount: '0' };
};

export const calculatePricingPolicy = (
  newDiscount: { discountAmount: number; discountType: 'percentage' | 'fixed' },
  computedPrice: { amount: string; currencyCode: string },
  subscriptionCurrencyCode: string,
  selectedVariantPrice: string,
): {
  basePrice: {
    amount: string;
    currencyCode: string;
  };
  cycleDiscounts?: CycleDiscounts[];
} => {
  const adjustmentType = discountTypeLookUp[newDiscount.discountType];

  let cycleDiscount = {
    adjustmentType,
    computedPrice,
    afterCycle: 0,
    adjustmentValue: {},
  };

  switch (adjustmentType) {
    case AdjustmentTypes.PERCENTAGE: {
      cycleDiscount = {
        ...cycleDiscount,
        adjustmentValue: {
          percentage: newDiscount.discountAmount,
        },
      };

      break;
    }
    case AdjustmentTypes.FIXED_AMOUNT: {
      cycleDiscount = {
        ...cycleDiscount,
        adjustmentValue: {
          amount: newDiscount.discountAmount,
          currencyCode: subscriptionCurrencyCode,
        },
      };
      break;
    }

    default:
      break;
  }

  return {
    basePrice: {
      amount: selectedVariantPrice,
      currencyCode: subscriptionCurrencyCode,
    },
    cycleDiscounts: newDiscount.discountType
      ? [cycleDiscount as CycleDiscounts]
      : undefined,
  };
};

const generateDefaultPricingPolicy = ({ currentPrice }): PricingPolicyApi => ({
  basePrice: currentPrice,
  cycleDiscounts: [
    {
      adjustmentType: AdjustmentTypes.PERCENTAGE,
      adjustmentValue: { percentage: 0 },
      afterCycle: 0,
      computedPrice: parseFloat(currentPrice),
    },
  ],
});

export const parsePricingPolicy = ({
  pricingPolicy,
  currentPrice,
}: {
  pricingPolicy: PricingPolicy;
  currentPrice?: number;
}): PricingPolicyApi => {
  if (isEmpty(pricingPolicy) || !pricingPolicy) {
    return generateDefaultPricingPolicy({ currentPrice });
  }
  const basePrice = parseFloat(pricingPolicy?.basePrice?.amount);
  let cycleDiscounts: CycleDiscountsApi[] = [];

  pricingPolicy.cycleDiscounts.map(({ __typename, ...discount }) => {
    const computedPrice = parseFloat(discount.computedPrice.amount);

    if (typeof computedPrice === 'number' && !isNaN(computedPrice)) {
      if (discount.adjustmentType === AdjustmentTypes.PERCENTAGE) {
        cycleDiscounts = [
          ...cycleDiscounts,
          {
            adjustmentType: discount.adjustmentType,
            afterCycle: discount.afterCycle,
            adjustmentValue: {
              percentage: discount.adjustmentValue.percentage,
            },
            computedPrice: parseFloat(discount.computedPrice.amount),
          },
        ];
      } else if (discount.adjustmentType === AdjustmentTypes.FIXED_AMOUNT) {
        cycleDiscounts = [
          ...cycleDiscounts,
          {
            adjustmentType: discount.adjustmentType,
            afterCycle: discount.afterCycle,
            adjustmentValue: {
              fixedValue: parseFloat(discount.adjustmentValue.amount),
            },
            computedPrice: parseFloat(discount.computedPrice.amount),
          },
        ];
      } else if (discount.adjustmentType === AdjustmentTypes.PRICE) {
        const value = discount.adjustmentValue;
        cycleDiscounts = [
          ...cycleDiscounts,
          {
            adjustmentType: discount.adjustmentType,
            afterCycle: discount.afterCycle,
            adjustmentValue: {
              fixedValue: parseFloat(value.amount || value.fixedValue),
            },
            computedPrice: parseFloat(discount.computedPrice.amount),
          },
        ];
      }
    }
  });
  return { basePrice, cycleDiscounts };
};

const generateNewCycleDiscounts = (cycleDiscounts, basePrice, currencyCode) =>
  cycleDiscounts.map((discount) => {
    switch (discount.adjustmentType) {
      case AdjustmentTypes.PERCENTAGE: {
        return {
          adjustmentType: AdjustmentTypes.PERCENTAGE,
          adjustmentValue: discount.adjustmentValue,
          afterCycle: discount.afterCycle,
          computedPrice: {
            amount: calculatePercentageDiscount(
              basePrice,
              discount.adjustmentValue.percentage,
            ),
            currencyCode: currencyCode,
          },
        };
      }
      case AdjustmentTypes.PRICE:
      case AdjustmentTypes.FIXED_AMOUNT: {
        return {
          adjustmentType: discount.adjustmentType,
          adjustmentValue: discount.adjustmentValue,
          afterCycle: discount.afterCycle,
          computedPrice: {
            amount: calculateFixedDiscount(
              basePrice,
              discount.adjustmentValue.amount,
            ),
            currencyCode: currencyCode,
          },
        };
      }
      default:
        break;
    }
  });

export const generateNewDynamicPricingPolicy = (
  oldPricingPolicy,
  selectedVariantPrice,
  newPrice,
  selectedLineCycleDiscounts,
  subscriptionCurrencyCode,
) => ({
  ...oldPricingPolicy,
  basePrice: {
    amount: parseFloat(selectedVariantPrice).toFixed(2),
    currencyCode: subscriptionCurrencyCode,
  },
  cycleDiscounts: generateNewCycleDiscounts(
    selectedLineCycleDiscounts,
    newPrice,
    subscriptionCurrencyCode,
  ),
});

export const isDefaultFrequency = (subscriptionContract): string => {
  const interval = get(subscriptionContract, 'billingPolicy.interval');
  const intervalCount = get(
    subscriptionContract,
    'billingPolicy.intervalCount',
  );

  const defaultInterval = Object.entries(CUSTOM_INTERVALS).find(
    ([_key, value]) => {
      return (
        value.interval === interval && value.intervalCount === intervalCount
      );
    },
  );

  return defaultInterval ? defaultInterval[0] : 'custom';
};

type Frequency = {
  interval: Intervals;
  intervalCount: number;
};

const batchConflict = {
  [BatchAnchors.Monthday]: [Intervals.Day, Intervals.Week],
  [BatchAnchors.Weekday]: [Intervals.Day],
};

export const detectBatchingConflicts = (
  frequency: Frequency,
  batchingSettings: BatchingAPI,
): boolean => {
  const { interval } = frequency;
  const { anchor } = batchingSettings;

  return !!batchConflict[anchor]?.includes(interval);
};

export const detectNextBillingBatchingConflicts = (
  nextBillingDate: string,
  batchingSettings: BatchingAPI,
): boolean => {
  const date = dayjs(nextBillingDate);
  const { anchor } = batchingSettings;
  if (anchor === BatchAnchors.Weekday) {
    const weekday = date.day() || 7; // Sunday is 0, but we store it as 7
    const weekdayBatching = batchingSettings.anchorWeek?.values || [];

    return !weekdayBatching.includes(weekday);
  } else if (anchor === BatchAnchors.Monthday) {
    const monthDayBatching = batchingSettings.anchorMonth?.values || [];
    const monthDay = date.date();

    return !monthDayBatching.includes(monthDay);
  } else if (anchor === BatchAnchors.Yearday) {
    const yearDayBatching = batchingSettings.anchorYear?.values || [];
    const yearDay = date.date();

    return !yearDayBatching.map(({ day }) => day).includes(yearDay);
  }

  return false;
};

export const getFilteredSellingPlansByInterval = (
  sellingPlans: SellingPlan[],
  interval: string,
): Array<{ label: string; value: string }> =>
  uniqBy(
    sellingPlans
      .filter((sp) => sp.billingPolicy?.interval === interval)
      .sort(
        (spA, spB) =>
          spA.billingPolicy?.intervalCount - spB.billingPolicy?.intervalCount,
      )
      .map((sp) => ({
        label: sp.options[0],
        value: `${sp.billingPolicy?.interval}#${sp.billingPolicy?.intervalCount}`,
      })),
    'value',
  );

export const cleanDefaultDisplayName = (displayName?: string): string =>
  displayName ? displayName.replace(/(- )?Default Title/i, '').trim() : '';

export const getNameFromParent = (
  parent: { title: string },
  title: string,
): string => (parent?.title ? `${parent.title} - ${title}` : title);

export const isAvailableForSale = (variant) => {
  if (!variant) {
    return false;
  }

  const {
    inventoryQuantity,
    sellableOnlineQuantity,
    inventoryManagement,
    inventoryPolicy,
  } = variant;
  const isInventoryTracked =
    inventoryManagement &&
    inventoryManagement.toUpperCase() !== INVENTORY_NOT_MANAGED;
  return isInventoryTracked
    ? (sellableOnlineQuantity ?? inventoryQuantity) > 0 ||
        inventoryPolicy?.toUpperCase() === INVENTORY_POLICY_CONTINUE
    : true;
};

const getVariantsFromBundle = (bundle) => {
  return (
    bundle?.babProducts?.reduce((acc, product) => {
      const variants = product?.variants?.map((variant) => ({
        ...variant,
        product,
      }));
      return [...acc, ...variants];
    }, []) || []
  );
};
export const getBundleSelectedItems = (customAttributes, bundle) => {
  const variants = getVariantsFromBundle(bundle);

  const PREFIX = '_pv_';
  return (
    customAttributes?.reduce((acc, { key, value }) => {
      if (key.startsWith(PREFIX)) {
        const variantId = key.replace(PREFIX, '');
        const id = getGidFromIdForResource(variantId, 'ProductVariant');
        const variant = variants.find((v) => v.id === id);
        const isVariantAvailableForSale = isAvailableForSale(variant);
        return variant && isVariantAvailableForSale
          ? acc + toNumber(value)
          : acc;
      }
      return acc;
    }, 0) || 0
  );
};

export const getBundleVariantsSelectedQuantity = ({
  customAttributes,
  bundle,
  isDynamicBundle,
  subscriptionLinePrices = {},
}) => {
  const variants = getVariantsFromBundle(bundle);
  const bundleSize = getBundleSelectedItems(customAttributes, bundle);
  const settings = bundle?.buildABoxSettings?.find(
    (item) => item.boxSize.toString() === bundleSize.toString(),
  );
  const hasSections = bundle?.sections?.length > 0;
  const isCollections = hasSections
    ? bundle.sections.every(({ collections }) => collections.length)
    : bundle?.collections?.length > 0;

  const pricePerItem =
    parseFloat(settings?.pricePerBox || '0') /
    parseFloat(settings?.boxSize || '1');

  const PREFIX = '_pv_';
  return (
    customAttributes?.reduce((acc, { key, value }) => {
      if (key.startsWith(PREFIX)) {
        const variantId = key.replace(PREFIX, '');
        const sectionsThatAllowsVariant = bundle?.sections
          ?.filter((section) => {
            const collectionIdsFromSection = section.collections.map(
              ({ id }) => id,
            );

            const productVariantIds = isCollections
              ? bundle.babProducts
                  ?.map(
                    ({ variants }) =>
                      variants?.map(({ id, collectionId }) => ({
                        id,
                        collectionId,
                      })),
                  )
                  .flat()
                  .filter((variant) =>
                    collectionIdsFromSection.includes(variant.collectionId),
                  )
                  .map(({ id }) => id)
              : section.products
                  .map(({ variants }) => variants.map(({ id }) => id))
                  .flat();

            return productVariantIds?.includes(
              getGidFromIdForResource(variantId, 'ProductVariant'),
            );
          })
          .map(({ id }) => id);

        const id = getGidFromIdForResource(variantId, 'ProductVariant');
        const variant = variants.find((v) => v.id === id);
        const isVariantAvailableForSale = isAvailableForSale(variant);
        const subscriptionLinePrice = subscriptionLinePrices[id as string];
        if (variant && isVariantAvailableForSale) {
          if (hasSections) {
            const sectionId =
              sectionsThatAllowsVariant.length > 0 &&
              sectionsThatAllowsVariant[0];
            acc[`${variantId}_${sectionId}`] = {
              quantity: toNumber(value),
              price: parseFloat(
                isDynamicBundle
                  ? subscriptionLinePrice?.pricingPolicyBasePrice ||
                      variant.price
                  : pricePerItem,
              ),
              title: cleanDefaultDisplayName(
                `${variant.product?.title} - ${variant.title}`,
              ),
            };
          } else {
            acc[variantId] = {
              quantity: toNumber(value),
              price: parseFloat(
                isDynamicBundle
                  ? subscriptionLinePrice?.pricingPolicyBasePrice ||
                      variant.price
                  : pricePerItem,
              ),
              title: cleanDefaultDisplayName(
                `${variant.product?.title} - ${variant.title}`,
              ),
            };
          }
        }
      }
      return acc;
    }, {}) || {}
  );
};

export const getNewBundlePayload = ({
  variant,
  bundle,
  selection,
  bundleSetting,
  hasDynamicSize,
}) => {
  const variants = getVariantsFromBundle(bundle);
  const selectedVariant = bundle.buildABoxSettings?.find(
    (setting) => setting.id === bundleSetting,
  );

  const bundleSize = Object.keys(selection)
    .reduce((acc, key) => acc + selection[key]?.quantity, 0)
    .toFixed(2);

  const bundlePrice = Object.keys(selection)
    .reduce(
      (acc, key) => acc + selection[key]?.price * selection[key]?.quantity,
      0,
    )
    .toFixed(2);

  const isDynamic = isDynamicBundle(bundle);
  const label = selectedVariant.label;
  const customAttributes = [
    {
      key: '_bundleName',
      value: variant.title,
    },
    {
      key: '_bundleLabel',
      value: label,
    },
    {
      key: '_bundleBoxSize',
      value: bundleSize,
    },
    {
      key: '_bundleBoxPrice',
      value: bundlePrice,
    },
    {
      key: '_bundleProductId',
      value: variant.product.id,
    },
    {
      key: '_variantId',
      value: selectedVariant.id,
    },
  ];

  if (isDynamic) {
    customAttributes.push(
      {
        key: '_bundleUid',
        value: uuidv4(),
      },
      {
        key: '_bundleHandle',
        value: bundle.handle,
      },
      {
        key: '_bundleType',
        value: 'dynamicBaB',
      },
    );

    if (hasDynamicSize) {
      if (selectedVariant.boxMinSize) {
        customAttributes.push({
          key: '_bundleBoxMinSize',
          value: selectedVariant.boxMinSize,
        });
      }

      if (selectedVariant.boxMaxSize) {
        customAttributes.push({
          key: '_bundleBoxMaxSize',
          value: selectedVariant.boxMaxSize,
        });
      }
    }
  }
  const hasSections = bundle?.sections?.length > 0;
  const newCustomAttributes = Object.keys(selection).reduce((acc, key) => {
    const { quantity, price } = selection[key];
    const value = quantity?.toString();
    const currentPrice = price.toFixed(2);
    const productVariantId = hasSections ? key.split('_')[0] : key;
    const gid = getGidFromIdForResource(productVariantId, 'ProductVariant');
    const variant = variants.find(({ id }) => id === gid);
    if (variant && value > 0) {
      const displayName = cleanDefaultDisplayName(
        `${variant.product.title} - ${variant.title}`,
      );
      return [
        ...acc,
        { key: `_pv_${productVariantId}`, value },
        { key: displayName, value },
        { key: `_title_${productVariantId}`, value: displayName },
        { key: `_price_${productVariantId}`, value: currentPrice },
      ];
    }

    return acc;
  }, customAttributes);

  return {
    id: selectedVariant.id,
    customAttributes: newCustomAttributes,
    displayName: cleanDefaultDisplayName(`${variant.title} - ${label}`),
    price: bundlePrice,
  };
};

export const normalizeBundleVariant = (variant) => {
  const { title, productType } = variant.product;

  return {
    ...variant,
    productType,
    title,
    isGrouper: false,
    hasParent: false,
    child: [],
  };
};

const getRegularBundleChangePayload = ({
  line,
  babApiLine,
  bundle,
  selection,
  bundleSetting,
  shouldPreserveManualPrice,
}) => {
  const { id, customAttributes, currentPrice } = line;
  const { currencyCode } = currentPrice;
  const hasSections = bundle?.sections?.length > 0;

  const variants = getVariantsFromBundle(bundle);
  const selectedVariant = bundle.buildABoxSettings?.find(
    (setting) => setting.id === bundleSetting,
  );

  if (!selectedVariant) {
    return null;
  }

  const bundleSize = selectedVariant.boxSize;

  const COPY_KEYS = ['_bundleName', '_bundleProductId', SC_LINE_MODS_ATTRIBUTE];
  let customAttributesInput = customAttributes
    .filter(({ key }) => COPY_KEYS.includes(key))
    .map(({ key, value }) => ({ key, value })); // to remove __typename key

  const selectedBaBSizeVariantId = selectedVariant.id;

  const newPrice = shouldPreserveManualPrice
    ? line.currentPrice.amount
    : selectedVariant.pricePerBox;

  customAttributesInput = [
    ...customAttributesInput,
    {
      key: '_bundleLabel',
      value: selectedVariant.label,
    },
    {
      key: '_bundleBoxSize',
      value: bundleSize?.toString(),
    },
    {
      key: '_bundleBoxPrice',
      value: newPrice,
    },
    {
      key: '_variantId',
      value: selectedBaBSizeVariantId,
    },
  ];

  customAttributesInput = Object.keys(selection).reduce((acc, key) => {
    const value = selection[key]?.quantity.toString();
    const productVariantId = hasSections ? key.split('_')[0] : key;
    const gid = getGidFromIdForResource(productVariantId, 'ProductVariant');
    const variant = variants.find(({ id }) => id === gid);
    if (variant && value > 0) {
      const displayName = cleanDefaultDisplayName(
        `${variant.product.title} - ${variant.title}`,
      );
      return [
        ...acc,
        { key: `_pv_${productVariantId}`, value },
        { key: displayName, value },
      ];
    }

    return acc;
  }, customAttributesInput);

  let currentPriceInput = newPrice;
  // To remove __typename key
  const parsedPricingPolicy = parsePricingPolicy({
    pricingPolicy: line.pricingPolicy,
    currentPrice: line.currentPrice.amount,
  });
  const pricingPolicy = generateNewDynamicPricingPolicy(
    parsedPricingPolicy,
    newPrice,
    newPrice,
    parsedPricingPolicy.cycleDiscounts,
    currencyCode,
  );
  const hasFixedPrice = babApiLine?.PrepaidVersion === 3;

  if (hasFixedPrice) {
    currentPriceInput = parseFloat(line.currentPrice.amount || '0').toFixed(2);
  } else if (pricingPolicy.cycleDiscounts.length > 0 && !babApiLine.PrepaidV2) {
    const cycleDiscount =
      getCycleDiscount(
        pricingPolicy.cycleDiscounts,
        babApiLine?.SuccessfulBillingAttempts,
      ) || pricingPolicy.cycleDiscounts[0];
    currentPriceInput = parseFloat(
      cycleDiscount.computedPrice?.amount || '0',
    ).toFixed(2);
  }

  const pricingPolicyInput = {
    basePrice: parseFloat(pricingPolicy.basePrice.amount).toFixed(2),
    cycleDiscounts: pricingPolicy.cycleDiscounts.map((cd) => {
      return {
        afterCycle: cd.afterCycle,
        computedPrice: parseFloat(cd.computedPrice.amount).toFixed(2),
        adjustmentType: cd.adjustmentType,
        adjustmentValue: cd.adjustmentValue,
      };
    }),
  };

  return [
    {
      id,
      updates: {
        productVariantId: selectedVariant.id,
        customAttributes: customAttributesInput,
        pricingPolicy: pricingPolicyInput,
        currentPrice: currentPriceInput,
      },
    },
  ];
};

const getDynamicBundleChangePayload = ({
  line,
  babApiLine,
  bundle,
  selection,
  bundleSetting,
  hasDynamicSize,
}) => {
  const hasSections = bundle?.sections?.length > 0;
  const selectionWithoutSections = Object.keys(selection).reduce((acc, key) => {
    const productVariantId = hasSections ? key.split('_')[0] : key;
    return {
      ...acc,
      [productVariantId]: selection[key],
    };
  }, {});

  const { customAttributes, currentPrice } = line;
  const { currencyCode } = currentPrice;
  const selectedVariant = bundle.buildABoxSettings?.find(
    (setting) => setting.id === bundleSetting,
  );

  if (!selectedVariant) {
    return null;
  }

  const COPY_KEYS = [
    '_bundleUid',
    '_bundleName',
    '_bundleType',
    '_bundleHandle',
    '_bundleProductId',
    SC_LINE_MODS_ATTRIBUTE,
  ];

  let customBaseAttributesInput = customAttributes
    .filter(({ key }) => COPY_KEYS.includes(key))
    .map(({ key, value }) => ({ key, value }));

  const bundleSize = customAttributes
    .reduce(
      (acc, attribute) =>
        acc + (attribute.key.startsWith('_pv_') ? +attribute.value : 0),
      0,
    )
    .toString();

  customBaseAttributesInput = [
    ...customBaseAttributesInput,
    {
      key: '_variantId',
      value: selectedVariant?.id,
    },
    {
      key: '_bundleLabel',
      value: selectedVariant?.label,
    },
    {
      key: '_bundleBoxSize',
      value: bundleSize,
    },
    {
      key: '_bundleBoxPrice',
      value: (
        parseFloat(selectedVariant.pricePerItem) * parseFloat(bundleSize)
      ).toFixed(2),
    },
  ];

  if (line.isDynamicBaB && hasDynamicSize) {
    customBaseAttributesInput = [
      ...customBaseAttributesInput,
      selectedVariant.boxMinSize
        ? {
            key: '_bundleBoxMinSize',
            value: selectedVariant.boxMinSize,
          }
        : null,
      selectedVariant.boxMaxSize
        ? {
            key: '_bundleBoxMaxSize',
            value: selectedVariant.boxMaxSize,
          }
        : null,
    ].filter(Boolean);
  }

  const parsedPricingPolicy = parsePricingPolicy({
    pricingPolicy: line.pricingPolicy,
    currentPrice: line.currentPrice.amount,
  });
  let payload = Object.keys(selectionWithoutSections).map((key) => {
    const line = customAttributes.find(
      ({ key: k, value }) => k.startsWith('_scl_') && value === key,
    );
    const newPrice = (selectionWithoutSections[key]?.price).toFixed(2);
    let currentPriceInput = newPrice;
    const id = line?.key.replace('_scl_', '');
    const pricingPolicy = generateNewDynamicPricingPolicy(
      parsedPricingPolicy,
      newPrice,
      newPrice,
      parsedPricingPolicy.cycleDiscounts,
      currencyCode,
    );

    if (pricingPolicy.cycleDiscounts.length > 0 && !babApiLine.PrepaidV2) {
      const cycleDiscount =
        getCycleDiscount(
          pricingPolicy.cycleDiscounts,
          babApiLine?.SuccessfulBillingAttempts,
        ) || pricingPolicy.cycleDiscounts[0];
      currentPriceInput = parseFloat(
        cycleDiscount.computedPrice?.amount || '0',
      ).toFixed(2);
    }

    const pricingPolicyInput = {
      basePrice: parseFloat(pricingPolicy.basePrice.amount).toFixed(2),
      cycleDiscounts: null,
    };

    const cycleDiscounts = pricingPolicy.cycleDiscounts.map((cd) => ({
      afterCycle: cd.afterCycle,
      computedPrice: parseFloat(cd.computedPrice.amount).toFixed(2),
      adjustmentType: cd.adjustmentType,
      adjustmentValue: cd.adjustmentValue,
    }));

    if (cycleDiscounts.length) {
      pricingPolicyInput.cycleDiscounts = cycleDiscounts;
    }

    return {
      id: getGidFromIdForResource(id, 'SubscriptionLine'),
      updates: {
        productVariantId: getGidFromIdForResource(key, 'ProductVariant'),
        variantTitle: selectionWithoutSections[key]?.title,
        customAttributes: [...customBaseAttributesInput],
        sellingPlanId: babApiLine?.SellingPlanId || null,
        quantity: selectionWithoutSections[key]?.quantity,
        pricingPolicy: pricingPolicyInput.cycleDiscounts
          ? pricingPolicyInput
          : null,
        currentPrice: currentPriceInput,
      },
    };
  });

  const sclines = customAttributes.filter(({ key }) => key.startsWith('_scl_'));

  // Remove scl that doesn't have a selected variant
  payload = sclines.reduce((acc, { key, value }) => {
    if (!selectionWithoutSections[value]) {
      return [
        ...acc,
        {
          id: getGidFromIdForResource(
            key.replace('_scl_', ''),
            'SubscriptionLine',
          ),
          updates: {
            quantity: 0,
          },
        },
      ];
    }

    return acc;
  }, payload);

  // Remove updates that don't have id and quantity is zero
  payload = payload
    .map(({ id: sclId, updates }) => {
      if (!sclId && updates.quantity === 0) {
        return null;
      }
      return { id: sclId, updates };
    })
    .filter(Boolean);

  return payload;
};

export const getBundleChangePayload = ({
  line,
  babApiLine,
  bundle,
  selection,
  bundleSetting,
  hasDynamicSize,
}): {
  id?: string;
  updates: {
    productVariantId: string;
    customAttributes: { key: string; value: string }[];
    pricingPolicy: {
      basePrice: string;
      cycleDiscounts: any;
    };
    sellingPlanId?: string;
    currentPrice: string;
    quantity?: number;
  };
}[] => {
  const shouldExpandDynamicBundle = isDynamicBundle(bundle);
  if (shouldExpandDynamicBundle) {
    return getDynamicBundleChangePayload({
      line,
      babApiLine,
      bundle,
      selection,
      bundleSetting,
      hasDynamicSize,
    });
  }

  return getRegularBundleChangePayload({
    line,
    babApiLine,
    bundle,
    selection,
    bundleSetting,
    shouldPreserveManualPrice:
      !line.pricingPolicy || isEmpty(line.pricingPolicy),
  });
};

export const getLineTitle = ({ title, variantTitle }) => {
  if (variantTitle) {
    return `${title} - ${variantTitle}`;
  }

  return title;
};

export const getDynamicBundleEditedEventPayload = ({
  lines,
  changes,
  toTotal,
  isPriceLabelHistoryEnabled,
}) => {
  const bundleUuid = changes
    .flatMap((c) => c.updates)
    ?.find(
      (line) => line.customAttributes?.find(({ key }) => key === '_bundleUid'),
    )
    ?.customAttributes?.find(({ key }) => key === '_bundleUid')?.value;

  const bundleLines = lines
    .filter((l) => !!isDynamicLine(l))
    .filter((l) =>
      l.customAttributes.find(
        (att) => att.key === '_bundleUid' && att.value === bundleUuid,
      ),
    );

  const fromLabel = bundleLines
    .find(
      ({ customAttributes }) =>
        customAttributes?.find(({ key }) => key === '_bundleLabel'),
    )
    ?.customAttributes?.find(({ key }) => key === '_bundleLabel')?.value;

  const fromTotal = bundleLines
    .reduce((acc, line) => acc + parseFloat(line.lineDiscountedPrice.amount), 0)
    .toFixed(2);
  const fromChanges = bundleLines.reduce(
    (acc, l) => {
      return {
        size: acc.size + parseFloat(l.quantity),
        products: [
          ...acc.products,
          [cleanDefaultDisplayName(getLineTitle(l)), parseFloat(l.quantity)],
        ],
      };
    },
    { size: 0, products: [] },
  );

  const toLabel = changes
    .find(({ updates }) =>
      updates.customAttributes.find(({ key }) => key === '_bundleLabel'),
    )
    ?.updates?.customAttributes?.find(({ key }) => key === '_bundleLabel')
    ?.value;
  const toChanges = changes.reduce(
    (acc, l) => {
      const quantity = parseFloat(l.updates?.quantity) || 0;
      const displayName = l.updates?.variantTitle;

      return quantity > 0
        ? {
            size: acc.size + quantity,
            products: [...acc.products, [displayName, quantity]],
          }
        : acc;
    },
    { size: 0, products: [] },
  );

  const haveSameAttributes = isEqual(fromChanges, toChanges);

  if (haveSameAttributes) {
    return null;
  }
  if (isPriceLabelHistoryEnabled) {
    return {
      from: { ...fromChanges, price: fromTotal, label: fromLabel },
      to: { ...toChanges, price: toTotal, label: toLabel },
    };
  } else {
    return {
      from: fromChanges,
      to: toChanges,
    };
  }
};

export const getBundleEditedEventPayload = ({
  lines,
  changes,
  isPriceLabelHistoryEnabled,
}) => {
  const [change] = changes;
  const id = change?.id;
  const customAttributes = change?.updates?.customAttributes;

  if (!id || !Array.isArray(customAttributes)) {
    return null;
  }

  const toObject = (attributes) =>
    attributes.reduce((memo, a) => {
      memo[a.key] = a.value;
      return memo;
    }, {});

  const parseBaBAttributes = (attributes) => {
    return Object.keys(attributes).reduce(
      (acc, key) => {
        if (key[0] !== '_') {
          acc.products.push([key, attributes[key]]);
        }
        return acc;
      },
      { size: attributes['_bundleBoxSize'], products: [] as [string, any][] },
    );
  };

  const babLine = lines?.find((line) => line.id === id);
  if (!babLine || !babLine.customAttributes || !customAttributes) {
    return null;
  }

  const from = toObject(babLine.customAttributes);
  const to = toObject(customAttributes);
  const haveSameAttributes = isEqual(from, to);

  if (haveSameAttributes) {
    return null;
  }
  if (isPriceLabelHistoryEnabled) {
    return {
      from: {
        ...parseBaBAttributes(from),
        price: from._bundleBoxPrice,
        label: from._bundleLabel,
      },
      to: {
        ...parseBaBAttributes(to),
        price: to._bundleBoxPrice,
        label: to._bundleLabel,
      },
    };
  } else {
    return {
      from: parseBaBAttributes(from),
      to: parseBaBAttributes(to),
    };
  }
};

export const getSellingPlansIntervalOptions = (sellingPlans) => {
  const hourIntervals = getFilteredSellingPlansByInterval(sellingPlans, 'HOUR');
  const dayIntervals = getFilteredSellingPlansByInterval(sellingPlans, 'DAY');
  const weekIntervals = getFilteredSellingPlansByInterval(sellingPlans, 'WEEK');
  const monthIntervals = getFilteredSellingPlansByInterval(
    sellingPlans,
    'MONTH',
  );
  const yearIntervals = getFilteredSellingPlansByInterval(sellingPlans, 'YEAR');

  return [
    ...hourIntervals,
    ...dayIntervals,
    ...weekIntervals,
    ...monthIntervals,
    ...yearIntervals,
  ];
};

export const createProductLineFromVariant = (variant) => {
  const {
    id,
    price,
    discountedPrice,
    discountType,
    displayName,
    customAttributes,
    hasParent,
    parent,
    title,
    isOneTimeAddon,
  } = variant;

  const product: ProductToAddWithName = {
    productVariantId: id,
    quantity: 1,
    isOneTimeAddon,
    currentPrice: price > 0 ? discountedPrice || price : Number(0).toFixed(2),
    displayName: !hasParent
      ? cleanDefaultDisplayName(displayName)
      : getNameFromParent(parent, title),
  };
  if (customAttributes) {
    product.customAttributes = customAttributes;
  }
  if (discountedPrice) {
    const key = discountType === 'percentage' ? 'percentage' : 'fixedValue';
    const adjustmentType =
      discountType === 'percentage' ? 'PERCENTAGE' : 'FIXED_AMOUNT';
    const discountAmount = parseFloat(price) - parseFloat(discountedPrice);
    const adjustmentValue =
      discountType === 'percentage'
        ? parseFloat(price) > 0
          ? (discountAmount * 100) / parseFloat(price)
          : 0
        : discountAmount;
    product['pricingPolicy'] = {
      basePrice: price,
      cycleDiscounts: {
        adjustmentType,
        adjustmentValue: {
          [key]: adjustmentValue,
        },
        afterCycle: 0,
        computedPrice: discountedPrice > 0 ? discountedPrice : 0,
      },
    };
  }

  return product;
};

export const createDynamicBundleLinesFromVariant = (variant) => {
  const { discountAmount, discountType, customAttributes } = variant;

  const baseCustomAttributes = customAttributes.filter(
    (attribute) =>
      attribute.key.startsWith('_bundle') ||
      attribute.key.startsWith('_variantId'),
  );
  const productVariantsToAdd = customAttributes.filter((attribute) =>
    attribute.key.startsWith('_pv_'),
  );

  return productVariantsToAdd.map((variant) => {
    const { key, value } = variant;
    const id = key.replace('_pv_', '');
    const price = customAttributes.find(
      (attribute) => attribute.key === `_price_${id}`,
    ).value;
    const displayName = customAttributes.find(
      (attribute) => attribute.key === `_title_${id}`,
    ).value;

    const currentPrice =
      discountType === 'percentage'
        ? parseFloat(price) > 0
          ? parseFloat(price) - (discountAmount * parseFloat(price)) / 100
          : 0
        : parseFloat(price) - discountAmount;

    const discountTypeKey =
      discountType === 'percentage' ? 'percentage' : 'fixedValue';
    const adjustmentType =
      discountType === 'percentage' ? 'PERCENTAGE' : 'FIXED_AMOUNT';
    const adjustmentValue = discountAmount > 0 ? parseFloat(discountAmount) : 0;

    const product: ProductToAddWithName = {
      productVariantId: getGidFromIdForResource(id, 'ProductVariant') as string,
      quantity: parseInt(value, 10),
      displayName: cleanDefaultDisplayName(displayName),
      currentPrice:
        currentPrice > 0 ? currentPrice.toFixed(2) : Number(0).toFixed(2),
      customAttributes: [...baseCustomAttributes],
      pricingPolicy: {
        basePrice: parseFloat(price).toFixed(2),
        cycleDiscounts: {
          adjustmentType,
          adjustmentValue: {
            [discountTypeKey]: adjustmentValue,
          },
          afterCycle: 0,
          computedPrice:
            currentPrice > 0 ? currentPrice.toFixed(2) : Number(0).toFixed(2),
        },
      },
    };

    return product;
  });
};

export const checkSectionLimits = ({ selection, bundle, bundleSetting }) => {
  let errors = [];
  //only validate selections with qty > 0
  const selectionWithQuantity = Object.keys(selection).reduce((acc, key) => {
    const item = selection[key];
    if (item.quantity > 0) {
      return { ...acc, [key]: item };
    }
    return { ...acc };
  }, {});

  const sectionsWithMinLimitOne = bundle?.sectionOverrides?.filter(
    ({ sectionLimit }) => {
      return (
        sectionLimit.limitType === LimitTypesEnum.MINIMUM &&
        sectionLimit.limitValueMin === 1
      );
    },
  );
  sectionsWithMinLimitOne.forEach(({ sizeAndSection, sectionLimit }) => {
    const selectionWithQuantitySectionIds = Object.keys(
      selectionWithQuantity,
    ).map((key) => key.split('_')[1]);
    const [sizeId, sectionId] = sizeAndSection.split('_');
    // Check if the section id that has a min 1 limit is in the selection, if its not it means that the user didn't select any product from that section
    const isSectionWithSelection =
      selectionWithQuantitySectionIds.includes(sectionId);
    if (!isSectionWithSelection && sizeId === bundleSetting) {
      errors = [
        ...errors,
        {
          type: LimitTypesEnum.MINIMUM,
          label: sectionLimit.limitLabel,
          sectionId: sizeAndSection.split('_')[1],
        },
      ];
    }
  });

  Object.keys(selectionWithQuantity).forEach((key) => {
    const [_, sectionId] = key.split('_');
    const section = bundle?.sections?.find(
      (section) => section.id === sectionId,
    );
    const sectionHasOverride = bundle.sectionOverrides?.find(
      ({ sizeAndSection }) => {
        const [size, section] = sizeAndSection.split('_');
        return size === bundleSetting && section === sectionId;
      },
    );
    if (section && sectionHasOverride) {
      const limitType = sectionHasOverride.sectionLimit.limitType;
      const qtyInSection = Object.keys(selection).reduce((acc, key) => {
        const [_variantId, sectionId] = key.split('_');

        if (sectionId === section.id) {
          return acc + selection[key].quantity;
        }
        return acc;
      }, 0);

      switch (limitType) {
        case LimitTypesEnum.MAXIMUM:
          if (qtyInSection > sectionHasOverride.sectionLimit.limitValueMax) {
            errors = [
              ...errors,
              {
                type: LimitTypesEnum.MAXIMUM,
                label: sectionHasOverride.sectionLimit.limitLabel,
                sectionId: sectionId,
              },
            ];
          }
          break;
        case LimitTypesEnum.MINIMUM:
          if (qtyInSection < sectionHasOverride.sectionLimit.limitValueMin) {
            errors = [
              ...errors,
              {
                type: LimitTypesEnum.MINIMUM,
                label: sectionHasOverride.sectionLimit.limitLabel,
                sectionId: sectionId,
              },
            ];
          }
          break;
        case LimitTypesEnum.BOTH:
          if (
            qtyInSection < sectionHasOverride.sectionLimit.limitValueMin ||
            qtyInSection > sectionHasOverride.sectionLimit.limitValueMax
          ) {
            errors = [
              ...errors,
              {
                type: LimitTypesEnum.BOTH,
                label: sectionHasOverride.sectionLimit.limitLabel,
                sectionId: sectionId,
              },
            ];
          }
          break;
      }
    }
  });
  return errors;
};

export const isPrepaidDiscount = (discount?: any): boolean => {
  if (!discount) return false;

  return discount.title?.includes('Prepaid');
};
